import React, { Suspense, useRef, useState, useEffect, memo, lazy } from 'react'
import { Canvas } from '@react-three/fiber'
import { Html, Environment, ContactShadows, OrbitControls } from '@react-three/drei'
import HeroPage from './HeroPage'
import { Helmet } from 'react-helmet'

// Lazy load the 3D components to reduce initial JS payload
const LazyModel = lazy(() => import('./Model3D'))

// ─── Data ─────────────────────────────────────────────────────────────────────
const data = {
  whatsapp: "https://wa.me/971502441327",
  linkedin:  "https://linkedin.com/in/moez-grami-98616812b",
  email:     "moez.grami1997@gmail.com",
  phone:     "+971 502441327",
  location:  "Business Bay, Dubai, UAE",
  nationality:"Tunisian",
  education: [
    { degree: "Software Engineering",       institution: "TekUP University", period: "2018 – 2021" },
    { degree: "Integrated Preparatory", institution: "TekUP University", period: "2016 – 2018" },
  ],
  skills: {
    "Front-End":      { pills:["NextJS","React","Vue","Angular","ThreeJS","Tailwind","NuxtJS","HTML/CSS"], c:{bg:"rgba(99,102,241,0.15)",bd:"rgba(99,102,241,0.45)",tx:"#a5b4fc"} },
    "Back-End":       { pills:["NestJS","ExpressJS","Node.js","ASP.NET","Laravel","OpenCart 4"],            c:{bg:"rgba(16,185,129,0.12)",bd:"rgba(16,185,129,0.4)",tx:"#6ee7b7"} },
    "Languages":      { pills:["TypeScript","JavaScript","Python","C#","PHP","C/C++"],                      c:{bg:"rgba(245,158,11,0.12)",bd:"rgba(245,158,11,0.35)",tx:"#fcd34d"} },
    "Database":       { pills:["MySQL","MongoDB","PostgreSQL"],                                              c:{bg:"rgba(236,72,153,0.12)",bd:"rgba(236,72,153,0.35)",tx:"#f9a8d4"} },
    "DevOps & Cloud": { pills:["Azure DevOps","Docker","AWS S3","CI/CD","Git"],                             c:{bg:"rgba(6,182,212,0.12)", bd:"rgba(6,182,212,0.35)", tx:"#67e8f9"} },
    "Tools":          { pills:["Jira","Figma","Postman","Swagger","Selenium"],                              c:{bg:"rgba(139,92,246,0.12)",bd:"rgba(139,92,246,0.35)",tx:"#c4b5fd"} },
  },
  certificates: [
    { name:"Professional Scrum Master™ (PSM I)",   icon:"🏆" },
    { name:"English onSET B2",                      icon:"🌐" },
    { name:"LPIC1 Exam 101 (Linux)",                icon:"🐧" },
    { name:"HTML/CSS MTA 98-383",                   icon:"💻" },
    { name:"JS Fundamentals MTA 98-382",            icon:"⚡" },
    { name:"Database Fundamentals MTA 98-364",      icon:"🗄️" },
  ],
  languages:[
    { lang:"Arabic",  level:"Native", pct:100 },
    { lang:"English", level:"Fluent", pct:90  },
    { lang:"French",  level:"Fluent", pct:90  },
  ],
}


// ─── Shared helpers ───────────────────────────────────────────────────────────
function SecTitle({ children }) {
  return (
    <div style={{ marginBottom:8, marginTop:2 }}>
      <div style={{ display:"flex", alignItems:"center", gap:6 }}>
        <div style={{ width:3, height:14, borderRadius:2, background:"linear-gradient(180deg,#6366f1,#06b6d4)", flexShrink:0 }}/>
        <span style={{ fontSize:13, fontWeight:800, textTransform:"uppercase", letterSpacing:1.8, color:"#64748b" }}>{children}</span>
      </div>
      <div style={{ height:1, background:"rgba(99,102,241,0.15)", marginTop:5 }}/>
    </div>
  )
}

const PANEL = {
  background:"rgba(10,17,35,0.88)", backdropFilter:"blur(22px)",
  border:"1px solid rgba(99,102,241,0.18)", borderRadius:14,
  padding:"18px 16px", color:"white",
  fontFamily:"'Outfit','DM Sans',sans-serif",
  boxShadow:"0 0 40px rgba(99,102,241,0.08),inset 0 1px 0 rgba(255,255,255,0.04)",
  position:"relative",
}

// ─── WhatsApp CTA ─────────────────────────────────────────────────────────────
function WACta({ fullWidth = false, isMobile = false }) {
  const [hov, setHov] = useState(false)
  return (
    <a href={data.whatsapp} target="_blank" rel="noopener noreferrer"
      onMouseEnter={()=>setHov(true)} onMouseLeave={()=>setHov(false)}
      style={{
        display:"flex", alignItems:"center", justifyContent:"center", gap:5,
        width: fullWidth ? "100%" : "auto",
        padding: isMobile ? "7px 12px" : "9px 18px", borderRadius:10, textDecoration:"none",
        background: hov ? "linear-gradient(135deg,#20c55a,#0f7a4e)" : "linear-gradient(135deg,#25d366,#128c7e)",
        color:"white", fontSize: isMobile ? 13 : 16, fontWeight:700, letterSpacing:0.4,
        boxShadow: hov ? "0 6px 28px rgba(37,211,102,0.55)" : "0 4px 20px rgba(37,211,102,0.35)",
        transition:"all 0.18s ease", transform: hov ? "scale(1.03)" : "scale(1)",
        cursor:"pointer",
      }}>
      <WAIcon isMobile={isMobile}/> Get in Touch
    </a>
  )
}
function WAIcon({ isMobile = false }) {
  return (
    <svg width={isMobile ? 10 : 14} height={isMobile ? 10 : 14} viewBox="0 0 24 24" fill="white">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
  )
}

// ─── Left Panel Content ───────────────────────────────────────────────────────
const LeftContent = memo(function LeftContent({ isMobile = false }) {
  const scale = isMobile ? 1.15 : 1
  return (
    <>
      {/* Avatar */}
      <div style={{ textAlign:"center", marginBottom:12 }}>
        <div style={{
          width:80 * scale, height:80 * scale, borderRadius:"50%",
          background:"linear-gradient(135deg,#6366f1,#06b6d4)",
          display:"flex", alignItems:"center", justifyContent:"center",
          margin:"0 auto 9px",
          boxShadow:"0 0 22px rgba(99,102,241,0.45)",
          border:"2px solid rgba(99,102,241,0.4)",
          overflow:"hidden",
        }}>
          <img src="/MoezGRAMI.webp" alt="Moez Grami" style={{ width:"100%", height:"100%", objectFit:"cover" }} />
        </div>
        <div style={{ fontSize:20 * scale, fontWeight:800, color:"#f1f5f9", letterSpacing:0.4 }}>Moez Grami</div>
        <div style={{ fontSize:13 * scale, color:"#6366f1", fontWeight:700, letterSpacing:1.4, textTransform:"uppercase", marginTop:2 }}>Senior Software Engineer</div>
        <div style={{
          display:"inline-block", marginTop:7, padding:"4px 10px", borderRadius:20,
          background:"rgba(99,102,241,0.12)", border:"1px solid rgba(99,102,241,0.28)",
          fontSize:13 * scale, color:"#a5b4fc", fontWeight:700,
        }}>6+ Years · Dubai 🇦🇪</div>
      </div>

      {/* Resume Download */}
      <a href="/moez_grami_resume_28052026.pdf" download style={{
        display:"block", marginTop:8, textDecoration:"none",
      }}>
        <div style={{
          display:"flex", alignItems:"center", justifyContent:"center", gap:8,
          padding:"12px 18px", borderRadius:10,
          background:"linear-gradient(135deg,#6366f1,#06b6d4)",
          border:"none", cursor:"pointer",
          fontSize:12 * scale, fontWeight:700, color:"white",
          letterSpacing:0.5, textTransform:"uppercase",
          boxShadow:"0 0 15px rgba(99,102,241,0.4)",
          transition:"all 0.3s ease",
        }} onMouseEnter={e=>e.currentTarget.style.transform="scale(1.02)"}
        onMouseLeave={e=>e.currentTarget.style.transform="scale(1)"}>
          <svg width={16 * scale} height={16 * scale} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
            <polyline points="7 10 12 15 17 10"/>
            <line x1="12" y1="15" x2="12" y2="3"/>
          </svg>
          Download Resume
        </div>
      </a>

      {/* WhatsApp CTA */}
      {/* <WACta fullWidth /> */}

      {/* Contact */}
      <div style={{ marginTop:12 }}>
        <SecTitle>Contact</SecTitle>
        {[
          { icon:"✉️", val:data.email,    href:`mailto:${data.email}` },
          { icon:"📱", val:data.phone,    href:`tel:${data.phone}` },
          { icon:"📍", val:data.location },
          { icon:"🔗", val:"LinkedIn",    href:data.linkedin },
          { icon:"🌍", val:data.nationality },
        ].map(({ icon,val,href })=>(
          <div key={val} style={{ display:"flex", gap:8, alignItems:"flex-start", marginBottom:6 }}>
            <span style={{ fontSize:14 * scale, flexShrink:0, marginTop:1 }}>{icon}</span>
            {href
              ? <a href={href} target="_blank" rel="noopener noreferrer"
                  style={{ fontSize:15 * scale, color:"#94a3b8", lineHeight:1.5, wordBreak:"break-all", textDecoration:"none" }}
                  onMouseEnter={e=>e.currentTarget.style.color="#a5b4fc"}
                  onMouseLeave={e=>e.currentTarget.style.color="#94a3b8"}>{val}</a>
              : <span style={{ fontSize:15 * scale, color:"#94a3b8", lineHeight:1.5 }}>{val}</span>
            }
          </div>
        ))}
      </div>

      {/* Education */}
      <div style={{ marginTop:10 }}>
        <SecTitle>Education</SecTitle>
        {data.education.map((edu,i)=>(
          <div key={i} style={{ marginBottom:10, paddingLeft:12, borderLeft:"2px solid rgba(99,102,241,0.4)", position:"relative" }}>
            <div style={{ position:"absolute", left:-5, top:5, width:8 * scale, height:8 * scale, borderRadius:"50%", background:"#6366f1", border:"2px solid #0a1020" }}/>
            <div style={{ fontSize:16 * scale, fontWeight:700, color:"#e2e8f0", lineHeight:1.4 }}>{edu.degree}</div>
            <div style={{ fontSize:14 * scale, color:"#94a3b8", marginTop:2 }}>{edu.institution}</div>
            <div style={{ fontSize:13 * scale, color:"#6366f1", fontWeight:600, marginTop:1 }}>{edu.period}</div>
          </div>
        ))}
      </div>

      {/* Languages */}
      <div style={{ marginTop:10 }}>
        <SecTitle>Languages</SecTitle>
        {data.languages.map(({ lang,level,pct })=>(
          <div key={lang} style={{ marginBottom:7 }}>
            <div style={{ display:"flex", justifyContent:"space-between", marginBottom:3 }}>
              <span style={{ fontSize:15 * scale, color:"#e2e8f0", fontWeight:600 }}>{lang}</span>
              <span style={{ fontSize:13 * scale, color:"#6366f1", fontWeight:600 }}>{level}</span>
            </div>
            <div style={{ height:3, borderRadius:3, background:"rgba(255,255,255,0.05)", overflow:"hidden" }}>
              <div style={{ width:`${pct}%`, height:"100%", background:"linear-gradient(90deg,#6366f1,#06b6d4)", borderRadius:3 }}/>
            </div>
          </div>
        ))}
      </div>

      {/* QR Code */}
      <div style={{ marginTop:10 }}>
        <SecTitle>QR Code "moezgrami.com"</SecTitle>
        <img src="/qr-code.png" alt="QR Code" style={{ width:"100%", height:"auto" }} />
      </div>
    </>
  )
})

// ─── Right Panel Content ──────────────────────────────────────────────────────
const RightContent = memo(function RightContent({ isMobile = false }) {
  const [open, setOpen] = useState(null)
  const scale = isMobile ? 1.15 : 1
  return (
    <>
      <SecTitle>Technical Skills</SecTitle>
      {Object.entries(data.skills).map(([cat,{ pills,c }])=>{
        const isOpen = open===cat
        return (
          <div key={cat} style={{ marginBottom:9 }}>
            <button onClick={()=>setOpen(isOpen?null:cat)} style={{
              width:"100%", background:"none", border:"none", cursor:"pointer",
              display:"flex", justifyContent:"space-between", alignItems:"center",
              padding:"5px 0",
            }}>
              <span style={{ fontSize:14 * scale, fontWeight:700, color:c.tx, textTransform:"uppercase", letterSpacing:0.9 }}>{cat}</span>
              <span style={{ fontSize:14 * scale, color:c.tx, display:"inline-block",
                transform: isOpen?"rotate(90deg)":"rotate(0deg)", transition:"transform 0.2s" }}>›</span>
            </button>
            {isOpen && (
              <div style={{ display:"flex", flexWrap:"wrap", gap:5, marginTop:6 }}>
                {pills.map(p=>(
                  <span key={p} style={{
                    fontSize:12 * scale, padding:"4px 10px", borderRadius:20,
                    background:c.bg, border:`1px solid ${c.bd}`, color:c.tx, fontWeight:600,
                  }}>{p}</span>
                ))}
              </div>
            )}
            <div style={{ height:1, background:"rgba(255,255,255,0.04)", marginTop:isOpen?9:0 }}/>
          </div>
        )
      })}

      <div style={{ marginTop:12 }}>
        <SecTitle>Certifications</SecTitle>
        {data.certificates.map((cert,i)=>(
          <div key={i} style={{
            display:"flex", alignItems:"flex-start", gap:8, marginBottom:7,
            padding:"7px 10px", background:"rgba(99,102,241,0.06)",
            border:"1px solid rgba(99,102,241,0.13)", borderRadius:8,
          }}>
            <span style={{ fontSize:13 * scale, flexShrink:0 }}>{cert.icon}</span>
            <span style={{ fontSize:13 * scale, color:"#cbd5e1", lineHeight:1.5 }}>{cert.name}</span>
          </div>
        ))}
      </div>

      <div style={{ marginTop:12 }}>
        <SecTitle>Key Competences</SecTitle>
        <div style={{ display:"flex", flexWrap:"wrap", gap:5 }}>
          {["Full-Stack Dev","System Design","Agile/Scrum","Cloud Integration",
            "Problem Solving","Leadership","CI/CD","ITIL"].map(c=>(
            <span key={c} style={{
              fontSize:12 * scale, padding:"4px 10px", borderRadius:20,
              background:"rgba(6,182,212,0.08)", border:"1px solid rgba(6,182,212,0.28)",
              color:"#67e8f9", fontWeight:600,
            }}>{c}</span>
          ))}
        </div>
      </div>
    </>
  )
})

// ─── Bottom Panel Content ───────────────────────────────────────────────────────
function BottomContent({ compact = false, isMobile = false }) {
  if (compact) {
    if (isMobile) {
      return (
        <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
          <div>
            <div style={{ fontSize:15, fontWeight:800, color:"#f1f5f9", marginBottom:3, lineHeight:1.3 }}>
              🚀 Ready to Bring Your Project to Life?
            </div>
            <div style={{ fontSize:12, color:"#b6bfccff", lineHeight:1.4 }}>
              Professional websites and e-commerce solutions.
            </div>
          </div>
          <WACta fullWidth isMobile={true} />
        </div>
      )
    }
    return (
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:16 }}>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:20, fontWeight:800, color:"#f1f5f9", marginBottom:4 }}>
            🚀 Ready to Bring Your Project to Life?
          </div>
          <div style={{ fontSize:14, color:"#b6bfccff", lineHeight:1.4 }}>
            Professional websites and e-commerce solutions that help your business grow, attract customers, and achieve measurable results.
          </div>
        </div>
        <WACta />
      </div>
    )
  }

  return (
    <div style={{ textAlign:"center" }}>
      <SecTitle>Let's Build Something Amazing</SecTitle>
      <p style={{
        fontSize:13, lineHeight:1.6, color:"#94a3b8", marginBottom:12,
      }}>
        Looking for a professional website or e-commerce solution? I specialize in building
        modern, scalable, and high-performance web applications tailored to your business needs.
      </p>
      <div style={{
        background:"rgba(99,102,241,0.08)", border:"1px solid rgba(99,102,241,0.2)",
        borderRadius:10, padding:"12px", marginBottom:12,
      }}>
        <div style={{ fontSize:11, color:"#6366f1", fontWeight:700, marginBottom:6, textTransform:"uppercase", letterSpacing:1 }}>
          Services I Offer
        </div>
        <div style={{ display:"flex", flexWrap:"wrap", gap:4, justifyContent:"center" }}>
          {["Custom Websites","E-commerce","Web Applications","API Development","UI/UX Design","Maintenance"].map(s=>(
            <span key={s} style={{
              fontSize:10, padding:"3px 8px", borderRadius:15,
              background:"rgba(6,182,212,0.1)", border:"1px solid rgba(6,182,212,0.3)",
              color:"#67e8f9", fontWeight:600,
            }}>{s}</span>
          ))}
        </div>
      </div>
      <WACta fullWidth />
      <div style={{ fontSize:10, color:"#64748b", marginTop:8 }}>
        🚀 Fast delivery · 💼 Professional quality · 🌍 Remote-friendly
      </div>
    </div>
  )
}

// ─── Pulsing Mobile Edge Button ───────────────────────────────────────────────
function EdgeBtn({ side, label, isOpen, onClick }) {
  const isLeft = side === "left"
  return (
    <button onClick={onClick} style={{
      position:"fixed",
      top:"50%", transform:"translateY(-50%)",
      [isLeft?"left":"right"]: isOpen ? (isLeft?"300px":"300px") : 0,
      zIndex:200,
      background: isOpen
        ? "rgba(15,23,42,0.95)"
        : "linear-gradient(135deg,#6366f1,#06b6d4)",
      border:"none", cursor:"pointer",
      borderRadius: isLeft ? "0 10px 10px 0" : "10px 0 0 10px",
      padding:"22px 8px",
      display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:6,
      boxShadow: isOpen ? "none" : "0 0 20px rgba(99,102,241,0.6)",
      transition:"all 0.3s ease",
      animation: isOpen ? "none" : "pulse 2s ease-in-out infinite",
    }}>
      {/* Arrow icon */}
      <svg width="16" height="16" viewBox="0 0 24 24" fill="white"
        style={{ transform: isLeft ? (isOpen?"rotate(180deg)":"rotate(0deg)") : (isOpen?"rotate(0deg)":"rotate(180deg)"), transition:"transform 0.3s" }}>
        <path d="M8 5l8 7-8 7" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
      </svg>
      {/* Vertical label */}
      <span style={{
        writingMode:"vertical-rl", textOrientation:"mixed",
        fontSize:17, fontWeight:800, color:"white", letterSpacing:2,
        textTransform:"uppercase",
      }}>{label}</span>
    </button>
  )
}

// ─── Mobile Drawer Panel ──────────────────────────────────────────────────────
function MobileDrawer({ side, isOpen, children }) {
  const isLeft = side === "left"
  return (
    <div style={{
      position:"fixed", top:0, [isLeft?"left":"right"]:0,
      width:300, height:"100%", zIndex:150,
      transform: isOpen ? "translateX(0)" : (isLeft?"translateX(-100%)":"translateX(100%)"),
      transition:"transform 0.32s cubic-bezier(0.4,0,0.2,1)",
      background:"rgba(7,14,30,0.97)", backdropFilter:"blur(24px)",
      borderRight: isLeft ? "1px solid rgba(99,102,241,0.2)" : "none",
      borderLeft: isLeft ? "none" : "1px solid rgba(99,102,241,0.2)",
      overflowY:"auto", padding:"18px 16px 24px",
      fontFamily:"'Outfit','DM Sans',sans-serif",
      scrollbarWidth:"thin", scrollbarColor:"rgba(99,102,241,0.3) transparent",
    }}>
      {children}
    </div>
  )
}

// ─── Desktop Panel ────────────────────────────────────────────────────────────
function DesktopPanel({ children, style }) {
  return (
    <div style={{
      ...PANEL, width:360, flexShrink:0,
      height:"calc(100vh - 32px)",
      overflowY:"auto", scrollbarWidth:"thin",
      scrollbarColor:"rgba(99,102,241,0.3) transparent",
      position:"fixed",
      ...style,
    }}>
      {children}
    </div>
  )
}

// ─── 3-D Canvas Section ───────────────────────────────────────────────────────
function LaptopCanvas({ disableControls = false }) {
  return (
    /* camera closer + slightly higher FOV so laptop fills more of the canvas */
    <Canvas camera={{ position:[-12,0,-11], fov:52 }} style={{ width:"100%", height:"100%", zIndex:0 }}>
      <pointLight position={[10,10,10]} intensity={1.5}/>
      <pointLight position={[-10,-5,-10]} intensity={0.3} color="#6366f1"/>
      <Suspense fallback={null}>
        <group rotation={[0,Math.PI,0]} position={[0,0.3,0]}>
          <LazyModel/>
        </group>
        <Environment preset="apartment" blur={0.5}/>
      </Suspense>
      <ContactShadows position={[0,-4.5,0]} scale={15} blur={3} far={3.5} opacity={0.4}/>
      <OrbitControls enablePan={false} enableZoom={true} enabled={!disableControls}
        minPolarAngle={Math.PI/2.2} maxPolarAngle={Math.PI/2.2}/>
    </Canvas>
  )
}

// ─── Root App ─────────────────────────────────────────────────────────────────
export default function App() {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 900 : false
  )
  const [leftOpen,  setLeftOpen]  = useState(false)
  const [rightOpen, setRightOpen] = useState(false)

  useEffect(() => {
    let rafId = null
    const handler = () => {
      if (rafId) cancelAnimationFrame(rafId)
      rafId = requestAnimationFrame(() => {
        setIsMobile(window.innerWidth < 900)
      })
    }
    window.addEventListener("resize", handler)
    return () => {
      if (rafId) cancelAnimationFrame(rafId)
      window.removeEventListener("resize", handler)
    }
  }, [])

  // Close panels when resizing to desktop
  useEffect(() => {
    if (!isMobile) { setLeftOpen(false); setRightOpen(false) }
  }, [isMobile])

  const closeAll = () => { setLeftOpen(false); setRightOpen(false) }

  return (
    <>
      <Helmet>
        <title>Moez Grami – Senior Software Engineer</title>
        <meta name="description" content="Moez Grami - Senior Software Engineer with 6+ years of experience in full-stack development, Azure DevOps, React, Next.js, and cloud integration. Building scalable web applications and e-commerce solutions in Dubai, UAE." />
        <link rel="icon" href="/MoezGRAMI.webp"/>
        <link href="https://fonts.googleapis.com/css2?family=Outfit:wght@400;600;700;800;900&family=DM+Sans:wght@400;500;600&display=swap" rel="stylesheet" media="print" onload="this.media='all'"/>
      </Helmet>

      {/* ── KEYFRAMES injected globally ── */}
      <style>{`
        @keyframes pulse {
          0%,100% { transform:translateY(-50%) scale(1); box-shadow:0 0 18px rgba(99,102,241,0.55); }
          50%      { transform:translateY(-50%) scale(1.12); box-shadow:0 0 28px rgba(99,102,241,0.85); }
        }
        @keyframes draghint {
          0%,100% { opacity:0.4; } 50% { opacity:0.9; }
        }
      `}</style>

      {/* ── DESKTOP LAYOUT ── */}
      {!isMobile && (
        <div style={{
          width:"100vw", height:"100vh",
          background:"linear-gradient(135deg,#060d1f 0%,#0d1627 55%,#0a1020 100%)",
          display:"flex", alignItems:"center", justifyContent:"center",
          gap:10, padding:"16px 12px", overflow:"hidden",
          fontFamily:"'Outfit','DM Sans',sans-serif",
          position:"relative",
        }}>
          {/* ambient centre glow */}
          <div style={{
            position:"absolute", top:"50%", left:"50%", transform:"translate(-50%,-50%)",
            width:500, height:350,
            background:"radial-gradient(ellipse,rgba(99,102,241,0.07) 0%,transparent 70%)",
            pointerEvents:"none", zIndex:0,
          }}/>

          {/* LEFT */}
          <DesktopPanel style={{ zIndex:9999, left:16 }}>
            <LeftContent/>
          </DesktopPanel>

          {/* CENTRE */}
          <div style={{ flex:1, height:"100%", position:"relative", zIndex:0, minWidth:0 }}>
            <div style={{
              position:"absolute", top:6, left:"50%", transform:"translateX(-50%)",
              fontSize:9, letterSpacing:3, textTransform:"uppercase",
              color:"rgba(165,180,252,0.5)", fontWeight:700, pointerEvents:"none", zIndex:10,
            }}>DRAG TO ROTATE</div>
            <LaptopCanvas disableControls={false}/>
            {/* <div style={{
              position:"absolute", bottom:6, left:"50%", transform:"translateX(-50%)",
              fontSize:8.5, letterSpacing:2, color:"rgba(148,163,184,0.38)",
              fontWeight:600, pointerEvents:"none", zIndex:10,
            }}>DRAG TO ROTATE</div> */}
          </div>

          {/* RIGHT */}
          <DesktopPanel style={{ zIndex:9999, right:16 }}>
            <RightContent/>
          </DesktopPanel>

          {/* BOTTOM */}
          <DesktopPanel style={{ zIndex:9999, bottom:16, left:400, right:400, width:"auto", transform:"none", height:"auto" }}>
            <BottomContent compact isMobile={false} />
          </DesktopPanel>
        </div>
      )}

      {/* ── MOBILE LAYOUT ── */}
      {isMobile && (
        <div style={{
          width:"100vw", height:"100vh",
          background:"linear-gradient(135deg,#060d1f 0%,#0d1627 55%,#0a1020 100%)",
          overflow:"hidden", position:"relative",
          fontFamily:"'Outfit','DM Sans',sans-serif",
        }}>
          {/* Dimming overlay when a drawer is open */}
          {(leftOpen||rightOpen) && (
            <div onClick={closeAll} style={{
              position:"fixed", inset:0, zIndex:100,
              background:"rgba(0,0,0,0.55)", backdropFilter:"blur(2px)",
            }}/>
          )}

          {/* Left drawer */}
          <MobileDrawer side="left" isOpen={leftOpen}>
            <LeftContent isMobile={true}/>
          </MobileDrawer>

          {/* Right drawer */}
          <MobileDrawer side="right" isOpen={rightOpen}>
            <RightContent isMobile={true}/>
          </MobileDrawer>

          {/* Left edge button */}
          <EdgeBtn
            side="left" label="Profile"
            isOpen={leftOpen}
            onClick={()=>{ setLeftOpen(o=>!o); setRightOpen(false) }}
          />

          {/* Right edge button */}
          <EdgeBtn
            side="right" label="Skills"
            isOpen={rightOpen}
            onClick={()=>{ setRightOpen(o=>!o); setLeftOpen(false) }}
          />

          {/* Full-screen laptop canvas */}
          <div style={{ width:"100%", height:"100%", position:"relative" }}>
            {/* Mobile name + WA */}
            <div style={{
              position:"absolute", top:16, left:"50%", transform:"translateX(-50%)",
              zIndex:10, textAlign:"center", pointerEvents:"none",
            }}>
              <div style={{ fontSize:8.5, color:"#6366f1", fontWeight:700, letterSpacing:1.5 }}>DRAG TO ROTATE</div>
            </div>

            <LaptopCanvas disableControls={leftOpen || rightOpen}/>

            {/* Bottom panel */}
            <div style={{
              position:"absolute", bottom:140, left:10, right:10,
              padding:"16px",
              background:"rgba(7,14,30,0.97)", backdropFilter:"blur(24px)",
              borderTop:"1px solid rgba(99,102,241,0.2)",
              zIndex:20,
            }}>
              <div style={{ textAlign:"left" }}>
                <div style={{ fontSize:16, fontWeight:800, color:"#f1f5f9", marginBottom:6, lineHeight:1.3 }}>
                  🚀 Ready to Bring Your Project to Life?
                </div>
                <div style={{ fontSize:13, color:"#b6bfccff", lineHeight:1.5, marginBottom:12 }}>
                  Professional websites and e-commerce solutions that help your business grow, attract customers, and achieve measurable results.
                </div>
                <a href="http://wa.me/971502441327" target="_blank" rel="noopener noreferrer" style={{
                  display:"inline-flex", alignItems:"center", justifyContent:"center", gap:8,
                  padding:"10px 20px", borderRadius:10, textDecoration:"none",
                  background:"linear-gradient(135deg,#25d366,#128c7e)",
                  color:"white", fontSize:15, fontWeight:700, letterSpacing:0.4,
                  boxShadow:"0 4px 20px rgba(37,211,102,0.35)",
                }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  Get in Touch
                </a>
              </div>
            </div>

          </div>
        </div>
      )}
    </>
  )
}
